import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { clearAuth } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";
import DispositionModal from "@/components/DispositionModal";
import ProspectInfoModal from "@/components/ProspectInfoModal";

const RunExitGuardContext = createContext(null);

// A run is considered "active" once prospects are entered and a runId exists.
// Forced disposition applies ONLY when attempting to leave the Runner.
export function RunExitGuardProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRunSandbox, setActiveRunSandbox] = useState(false);
  const [activeRunCollectAt, setActiveRunCollectAt] = useState("start"); // start | disposition
  const [defaultProspectNames, setDefaultProspectNames] = useState([]);
  const defaultProspectNamesRef = useRef([]);
  const [finalNodeId, setFinalNodeId] = useState(null);

  // End-of-run Prospect Info (for collect-at-disposition)
  const [prospectInfoOpen, setProspectInfoOpen] = useState(false);
  const [pendingDisposition, setPendingDisposition] = useState(null);
  const [prefillLeadId, setPrefillLeadId] = useState(null);
  const [prefillLeadLabel, setPrefillLeadLabel] = useState("");
  const [prefillProspects, setPrefillProspects] = useState([]);

  // manualExit:
  //  - { type: 'navigate', to: string }
  //  - { type: 'logout' }
  //  - { type: 'callback', run: () => void }
  const [manualExit, setManualExit] = useState(null);

  // While we are completing a disposition + redirecting, keep the modal closed
  // even if router state is still in transition.
  const [isExiting, setIsExiting] = useState(false);

  // When true, we allow exactly one navigation to proceed without blocking.
  // This prevents the "disposition twice" loop due to React state update timing.
  const [bypassOnce, setBypassOnce] = useState(false);

  const pendingProceedRef = useRef(null);

  const isOnRunner = useMemo(() => {
    const p = location.pathname || "";
    return p.includes("/app/scripts/") && p.includes("/play");
  }, [location.pathname]);

  // Only block exits when we are currently in the runner AND a run is active.
  const isInRun = Boolean(activeRunId) && isOnRunner;

  // React Router v7 navigation blocking (requires Data Router)
  const blocker = useBlocker(isInRun && !bypassOnce);
  const routerBlocked = blocker.state === "blocked";

  const pendingExit = useMemo(() => {
    if (isExiting) return null;

    if (routerBlocked && blocker.location) {
      const to =
        blocker.location.pathname +
        blocker.location.search +
        blocker.location.hash;
      return { type: "router_block", to };
    }

    return manualExit;
  }, [isExiting, routerBlocked, blocker.location, manualExit]);

  const modalOpen = Boolean(pendingExit);

  useEffect(() => {
    if (!isInRun) return;

    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Select an End of Run disposition before closing";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isInRun]);

  // Reset bypass after a successful location change.
  useEffect(() => {
    if (!bypassOnce) return;
    const t = setTimeout(() => setBypassOnce(false), 0);
    return () => clearTimeout(t);
  }, [bypassOnce, location.pathname, location.search, location.hash]);

  // Perform the post-disposition action only AFTER bypassOnce is applied.
  useEffect(() => {
    if (!bypassOnce) return;
    if (!pendingProceedRef.current) return;

    const next = pendingProceedRef.current;
    pendingProceedRef.current = null;

    try {
      if (next?.type === "router_block") {
        if (typeof blocker.proceed === "function") {
          blocker.proceed();
        }
        return;
      }

      if (next?.type === "navigate") {
        navigate(next.to);
        return;
      }

      if (next?.type === "logout") {
        clearAuth();
        toast({ title: "Signed out" });
        navigate("/login", { replace: true });
        return;
      }

      if (next?.type === "callback") {
        next.run?.();
      }
    } finally {
      setTimeout(() => setIsExiting(false), 0);
    }
  }, [bypassOnce, blocker, navigate]);

  const clearGuardState = useCallback(() => {
    setActiveRunId(null);
    setActiveRunSandbox(false);
    setActiveRunCollectAt("start");
    if ((defaultProspectNamesRef.current || []).length) {
      defaultProspectNamesRef.current = [];
      setDefaultProspectNames([]);
    }
    setFinalNodeId(null);

    setProspectInfoOpen(false);
    setPendingDisposition(null);
    setPrefillLeadId(null);
    setPrefillLeadLabel("");
    setPrefillProspects([]);
  }, []);

  const cancelExit = useCallback(() => {
    if (routerBlocked && typeof blocker.reset === "function") {
      blocker.reset();
    }
    setManualExit(null);
  }, [blocker, routerBlocked]);

  const requestLogout = useCallback(() => {
    setManualExit({ type: "logout" });
  }, []);


  const shouldCollectProspectInfoAtDisposition =
    String(activeRunCollectAt || "start") === "disposition";

  const loadRunPrefill = useCallback(async () => {
    if (!activeRunId) return;
    try {
      const res = await api.get(`/runs/${activeRunId}`);
      const run = res.data?.run || {};
      const prospects = run?.prospects || [];

      setPrefillLeadId(run.lead_id || null);
      setPrefillLeadLabel(run.lead_full_name || run.lead_id || "");
      setPrefillProspects(
        prospects.length
          ? prospects.map((p) => p?.name).filter(Boolean)
          : (defaultProspectNames || []),
      );
    } catch {
      setPrefillProspects(defaultProspectNames || []);
    }
  }, [activeRunId, defaultProspectNames]);

  const requestNavigate = useCallback((to) => {
    setManualExit({ type: "navigate", to });
  }, []);

  const requestAction = useCallback((run) => {
    setManualExit({ type: "callback", run });
  }, []);

  const persistDisposition = useCallback(
    async (disposition) => {
      if (!activeRunId) return;
      await api.post(`/runs/${activeRunId}/disposition`, {
        disposition,
        final_node_id: finalNodeId || undefined,
      });
    },
    [activeRunId, finalNodeId],
  );

  const persistProspectInfo = useCallback(
    async ({ selected_lead_id, lead_name, prospect_names }) => {
      if (!activeRunId) return;

      let leadId = selected_lead_id || null;
      const leadName = String(lead_name || "").trim();

      if (!leadId) {
        const res = await api.post("/leads", { full_name: leadName || "Prospect" });
        leadId = res.data?.id;
      }

      if (leadId) {
        await api.post(`/runs/${activeRunId}/link-lead`, { lead_id: leadId });
      }

      const names = (prospect_names || []).map((n) => String(n || "").trim()).filter(Boolean);
      if (names.length > 0) {
        await api.post(`/runs/${activeRunId}/prospects`, {
          prospects: names.map((name) => ({ name, lead_id: leadId })),
        });
      }
    },
    [activeRunId],
  );

  const completeExitWithDisposition = useCallback(
    async (disposition) => {
      const next = pendingExit;
      if (!next) return;

      setIsExiting(true);

      try {
        await persistDisposition(disposition);
      } catch (e) {
        setIsExiting(false);
        toast({
          title: "Could not save disposition",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Close modal state immediately for manual exits.
      setManualExit(null);

      // Unlock run.
      clearGuardState();

      // The key: apply bypassOnce in render BEFORE navigating/proceeding.
      pendingProceedRef.current = next;
      setBypassOnce(true);
    },
    [clearGuardState, pendingExit, persistDisposition],
  );



  const onDispositionSelected = useCallback(
    async (disposition) => {
      if (!shouldCollectProspectInfoAtDisposition) {
        await completeExitWithDisposition(disposition);
        return;
      }

      setPendingDisposition(disposition);
      await loadRunPrefill();
      setProspectInfoOpen(true);
    },
    [completeExitWithDisposition, loadRunPrefill, shouldCollectProspectInfoAtDisposition],
  );

  const value = useMemo(
    () => ({
      isInRun,
      activeRunId,
      activeRunSandbox,
      activeRunCollectAt,
      defaultProspectNames,
      finalNodeId,
      setActiveRun: ({ runId, sandbox, collectAt, defaultProspects }) => {
        setActiveRunId(runId || null);
        setActiveRunSandbox(Boolean(sandbox));
        setActiveRunCollectAt(String(collectAt || "start"));
        const nextDefaults = Array.isArray(defaultProspects) ? defaultProspects : [];
        const prevDefaults = defaultProspectNamesRef.current || [];
        const sameDefaults =
          prevDefaults.length === nextDefaults.length &&
          prevDefaults.every((v, i) => v === nextDefaults[i]);

        if (!sameDefaults) {
          defaultProspectNamesRef.current = nextDefaults;
          setDefaultProspectNames(nextDefaults);
        }
      },
      clearActiveRun: () => clearGuardState(),


      setFinalNodeId: (nodeId) => setFinalNodeId(nodeId || null),
      requestLogout,
      requestNavigate,
      requestAction,
      cancelExit,
    }),
    [
      isInRun,
      activeRunId,
      activeRunSandbox,
      activeRunCollectAt,
      defaultProspectNames,
      cancelExit,
      clearGuardState,
      finalNodeId,
      requestLogout,
      requestNavigate,
      requestAction,
    ],
  );

  return (
    <RunExitGuardContext.Provider value={value}>
      {children}
      <DispositionModal
        open={modalOpen}
        isSandbox={activeRunSandbox}
        onReturnToRunner={cancelExit}
        onSelectDisposition={(d) => onDispositionSelected(d)}
      />

      <ProspectInfoModal
        open={prospectInfoOpen}
        onOpenChange={(v) => {
          if (!v) {
            setProspectInfoOpen(false);
            setPendingDisposition(null);
            cancelExit();
          }
        }}
        title="Prospect Info"
        defaultProspectNames={prefillProspects}
        defaultLeadId={prefillLeadId}
        defaultLeadLabel={prefillLeadLabel}
        lockedProspects={null}
        requireLead={true}
        onSubmit={async (info) => {
          const next = pendingExit;
          const dispo = pendingDisposition;
          if (!next || !dispo) return;

          setIsExiting(true);
          try {
            await persistProspectInfo(info);
            await persistDisposition(dispo);
          } catch (e) {
            setIsExiting(false);
            toast({
              title: "Could not save prospect info",
              description: e?.response?.data?.detail || "Please try again.",
              variant: "destructive",
            });
            return;
          }

          setProspectInfoOpen(false);
          setPendingDisposition(null);

          setManualExit(null);
          clearGuardState();

          pendingProceedRef.current = next;
          setBypassOnce(true);
        }}
      />
    </RunExitGuardContext.Provider>
  );
}

export function useRunExitGuard() {
  const ctx = useContext(RunExitGuardContext);
  if (!ctx) {
    throw new Error("useRunExitGuard must be used within RunExitGuardProvider");
  }
  return ctx;
}
